import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "../components/providers/AuthProvider";
import { api } from "../utils/api";

export default function StripeReturn() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();

  // Get session ID from URL
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const id = urlParams.get("session_id");
    setSessionId(id);
  }, []);

  // Use tRPC query to get session status
  const {
    data: sessionData,
    isLoading,
    error,
  } = (api as any).stripe.getSessionStatus.useQuery(
    { sessionId: sessionId! },
    {
      enabled: !!sessionId,
      retry: 3,
      retryDelay: 1000,
    },
  );

  // TODO: Add state for redeem token when webhook implementation is complete
  // const [redeemToken, setRedeemToken] = useState<string | null>(null);
  // const [tokenError, setTokenError] = useState<string | null>(null);

  // TODO: Remove this when Stripe webhook is implemented
  // For now, we'll check if a redeem token should be shown for unauthenticated users
  useEffect(() => {
    if (sessionData?.status === "complete" && !authLoading && !user) {
      // Note: Redeem token will be created by the Stripe webhook, not client-side
      // This is a temporary placeholder until webhook implementation is complete
      console.log(
        "Payment complete for unauthenticated user - redeem token should be created by webhook",
      );
    }
  }, [sessionData, authLoading, user]);

  // Handle loading state
  if (isLoading || !sessionData) {
    return (
      <div className="min-w-screen w-screen min-h-screen h-full flex flex-col bg-surface-one">
        <div className="flex-1 flex flex-col items-center justify-center px-2 py-2">
          <div className="w-full max-w-4xl h-full flex flex-col grow bg-white rounded-2xl">
            <div className="grow mx-auto flex flex-col items-center justify-center mt-8 mb-8 px-4 gap-8">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
                <p className="text-lg text-foreground-secondary">
                  Processing your payment...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="min-w-screen w-screen min-h-screen h-full flex flex-col bg-surface-one">
        <div className="flex-1 flex flex-col items-center justify-center px-2 py-2">
          <div className="w-full max-w-4xl h-full flex flex-col grow bg-white rounded-2xl">
            <div className="grow mx-auto flex flex-col items-center justify-center mt-8 mb-8 px-4 gap-8">
              <div className="flex flex-col items-center justify-center text-center">
                <p className="text-lg text-red-600 mb-4">
                  Error retrieving payment status. Please contact support.
                </p>
                <a
                  href="/"
                  className="inline-flex items-center px-6 py-3 bg-brand-primary text-white rounded-full hover:bg-brand-primary-darker transition-colors"
                >
                  Return to Home
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (sessionData.status === "open") {
    return <Navigate to="/stripe-checkout" />;
  }

  if (sessionData.status === "complete") {
    return (
      <div className="min-w-screen w-screen min-h-screen h-full flex flex-col bg-surface-one">
        <div className="flex-1 flex flex-col items-center justify-center px-2 py-2">
          <div className="w-full max-w-4xl h-full flex flex-col grow bg-white rounded-2xl">
            <div className="grow mx-auto flex flex-col items-center justify-center mt-8 mb-8 px-4 gap-8">
              {/* Success Header */}
              <div className="flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-sm uppercase text-green-600">
                  Payment Successful
                </p>
                <p className="text-4xl text-foreground-primary">
                  Thank You for
                </p>
                <p className="text-4xl font-bold text-foreground-primary">
                  Your Purchase!
                </p>
              </div>

              {/* Success Message */}
              <section id="success" className="max-w-2xl text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <p className="text-lg text-foreground-primary mb-4">
                    We appreciate your business! A confirmation email will be
                    sent to{" "}
                    <span className="font-semibold text-brand-primary">
                      {sessionData.customer_email}
                    </span>
                    .
                  </p>
                  <p className="text-foreground-secondary">
                    If you have any questions, please email{" "}
                    <a
                      href="mailto:support@samphireneuro.com"
                      className="text-brand-primary hover:text-brand-primary-darker underline"
                    >
                      support@samphireneuro.com
                    </a>
                    .
                  </p>
                </div>

                {/* TODO: Redeem Token for Unauthenticated Users */}
                {/* This will be implemented when Stripe webhook creates the redeem token */}
                {!user && (
                  <div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="text-xl font-semibold text-blue-900 mb-4">
                      🎉 Your Membership Access
                    </h3>
                    <div className="text-left space-y-2 text-sm text-blue-800">
                      <p className="font-medium">
                        Your access code will be sent to your email shortly.
                      </p>
                      <p className="font-medium">
                        How to redeem your access code:
                      </p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Create an account or log in to Samphire</li>
                        <li>Go to Account Management</li>
                        <li>
                          Enter your access code to activate your membership
                        </li>
                      </ol>
                      <p className="text-xs text-blue-600 mt-3">
                        💡 Check your email for the access code. If you don't
                        receive it within a few minutes, please contact support.
                      </p>
                    </div>
                  </div>
                )}

                {/* Next Steps */}
                <div className="mt-8 p-6 bg-brand-primary/5 rounded-lg">
                  <h3 className="text-xl font-semibold text-foreground-primary mb-4">
                    What's Next?
                  </h3>
                  <div className="text-left space-y-3 text-foreground-secondary">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-brand-primary text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                        1
                      </div>
                      <p>
                        Check your email for order confirmation and tracking
                        information
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-brand-primary text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                        2
                      </div>
                      <p>
                        Download the Samphire app to get started with your
                        health journey
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-brand-primary text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                        3
                      </div>
                      <p>
                        Your device will be shipped within 2-3 business days
                      </p>
                    </div>
                  </div>
                </div>

                {/* Return Home Button */}
                <div className="mt-8">
                  <a
                    href="/"
                    className="inline-flex items-center px-6 py-3 bg-brand-primary text-white rounded-full hover:bg-brand-primary-darker transition-colors"
                  >
                    Return to Home
                  </a>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback for any other status
  return (
    <div className="min-w-screen w-screen min-h-screen h-full flex flex-col bg-surface-one">
      <div className="flex-1 flex flex-col items-center justify-center px-2 py-2">
        <div className="w-full max-w-4xl h-full flex flex-col grow bg-white rounded-2xl">
          <div className="grow mx-auto flex flex-col items-center justify-center mt-8 mb-8 px-4 gap-8">
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-lg text-foreground-secondary mb-4">
                Payment status: {sessionData.status}
              </p>
              <a
                href="/"
                className="inline-flex items-center px-6 py-3 bg-brand-primary text-white rounded-full hover:bg-brand-primary-darker transition-colors"
              >
                Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
